import { HollowScriptTooling } from './native/hollowscript.js';

// A main-thread singleton of the vendored `tooling` wasm build, separate
// from `nativeScriptRunner.ts`'s own copy: that one runs inside the tier-1
// Worker, a different JS realm with its own wasm memory - there is no way
// to share one instance across the two, so Monaco's diagnostics/formatting
// necessarily load a second copy here. See `nativeScriptRunner.ts`'s top
// doc comment for the full reasoning behind loading it here at all (the
// editor services need the precise spans `sstk-hollowscript-lang` commit
// `96966b9` added and the analysis handle commit `d578440` added).
//
// Deliberately a sibling of `nativeScriptRunner.ts`, in
// `src/engine/scripting/` directly, not under `monaco/` (where every
// caller of this file actually lives): `./native/<file>` below must be
// correct both under Node (Vitest's `file:` fallback reads it straight off
// this file's own real location on disk) and once bundled (Vite flattens
// every chunk into `assets/`, alongside the copied wasm files - confirmed
// directly against `nativeScriptRunner.ts`'s own build output). Only a
// true sibling of `native/` satisfies both at once; a first attempt at
// this file lived under `monaco/` and needed a *different* relative path
// for each context, which a single `import.meta.url`-relative literal
// cannot express - moved here instead of solving that properly, since
// there was no reason for two different files to.

export type MonacoTooling = HollowScriptTooling;

const toolingWasmAssetPath = './native/hollowscript_wasm_tooling.wasm';
const TOOLING_WASM_URL = new URL(toolingWasmAssetPath, import.meta.url);

let toolingPromise: Promise<MonacoTooling> | undefined;

async function fetchBytes(url: URL): Promise<Uint8Array> {
  if (url.protocol === 'file:') {
    const { readFile } = await import('node:fs/promises');
    return new Uint8Array(await readFile(url));
  }
  const response = await fetch(url);
  return new Uint8Array(await response.arrayBuffer());
}

/** Lazily loads and caches the main thread's own `tooling` wasm instance,
 * shared by every editor service in `monaco/`. `check`, `format`, `highlight`
 * and the rest are stateless per call, so one instance safely serves them
 * all; the one stateful piece, the `Analysis` handle `analyse` makes, is owned
 * by `monaco/analysisPool.ts`, which is what keeps the number of live handles
 * under the module's limit. */
export function getMonacoTooling(): Promise<MonacoTooling> {
  toolingPromise ??= fetchBytes(TOOLING_WASM_URL).then((bytes) => HollowScriptTooling.fromBytes(bytes));
  return toolingPromise;
}
